import { Button } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { useNavigateWithContentFlush } from '@/hooks';
import { useDocumentRender } from '@/hooks/useContentQueries';
import {
  useCreateDocumentChild,
  useCreateDocumentSibling,
  useDeleteDocumentSubtree,
  useMoveDocumentNode
} from '@/hooks/useContentQueries';
import { useDocumentScrollStore } from '@/store/documentScrollStore';
import { contentKeys } from '@/hooks/useContentQueries';
import { cn } from '@/lib/utils';
import { toContentHref } from '@/utils/content-ref';
import { Document, DocumentHierarchyTreeNode } from '@vivport/backend/types';
import { Check, ChevronDown, ChevronRight, FileText, MoreHorizontal, X } from 'lucide-react';
import { KeyboardEvent, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { Notice } from '@/components/ui/notice';

type DocumentStructureSectionProps = {
  document: Document;
  collapsed: boolean;
  onToggleCollapsed: () => void;
};

type TreeIndexEntry = {
  node: DocumentHierarchyTreeNode;
  parentId: number | null;
  depth: number;
  indexWithinParent: number;
  siblingCount: number;
};

type InlineCreateState = {
  mode: 'child' | 'sibling';
  anchorId: number;
  parentDocumentId: number;
  sortOrder: number;
  value: string;
};

type DeleteDialogState = {
  documentId: number;
  title: string;
  estimatedCount: number;
  subtreeIds: number[];
};

type DeleteBackendSummary = {
  deletedRootId: number;
  deletedCount: number;
  deletedTitle?: string;
};

const SHOW_NUMBERING_KEY = 'documentStructure.showNumbering.v1';
const EXPANDED_BY_ROOT_KEY = 'documentStructure.expandedByRoot.v2';

function getNodeChildren(node: DocumentHierarchyTreeNode | undefined): DocumentHierarchyTreeNode[] {
  if (!node || !Array.isArray(node.children)) {
    return [];
  }
  return node.children.filter((child): child is DocumentHierarchyTreeNode => Boolean(child));
}

function buildTreeIndex(root: DocumentHierarchyTreeNode | null): Map<number, TreeIndexEntry> {
  const index = new Map<number, TreeIndexEntry>();
  if (!root) {
    return index;
  }

  const visit = (node: DocumentHierarchyTreeNode, parentId: number | null, depth: number, position: number, siblingCount: number) => {
    index.set(node.id, {
      node,
      parentId,
      depth,
      indexWithinParent: position,
      siblingCount
    });
    const children = getNodeChildren(node);
    children.forEach((child, childIndex) => {
      visit(child, node.id, depth + 1, childIndex, children.length);
    });
  };

  visit(root, null, 0, 0, 1);
  return index;
}

function findNode(root: DocumentHierarchyTreeNode | null, targetId: number): DocumentHierarchyTreeNode | null {
  if (!root) {
    return null;
  }
  if (root.id === targetId) {
    return root;
  }
  const stack: DocumentHierarchyTreeNode[] = [root];
  while (stack.length > 0) {
    const current = stack.pop();
    if (!current) {
      break;
    }
    const children = getNodeChildren(current);
    for (let index = children.length - 1; index >= 0; index -= 1) {
      const child = children[index];
      if (child.id === targetId) {
        return child;
      }
      stack.push(child);
    }
  }
  return null;
}

function collectSubtreeIds(node: DocumentHierarchyTreeNode | null): number[] {
  if (!node) {
    return [];
  }
  const ids: number[] = [];
  const stack: DocumentHierarchyTreeNode[] = [node];
  while (stack.length > 0) {
    const current = stack.pop();
    if (!current) {
      break;
    }
    ids.push(current.id);
    const children = getNodeChildren(current);
    for (let index = children.length - 1; index >= 0; index -= 1) {
      stack.push(children[index]);
    }
  }
  return ids;
}

function collectTreeNodeIdsPreOrder(root: DocumentHierarchyTreeNode | null): number[] {
  if (!root) {
    return [];
  }
  const ids: number[] = [];
  const stack: DocumentHierarchyTreeNode[] = [root];
  while (stack.length > 0) {
    const current = stack.pop();
    if (!current) {
      break;
    }
    ids.push(current.id);
    const children = getNodeChildren(current);
    for (let index = children.length - 1; index >= 0; index -= 1) {
      stack.push(children[index]);
    }
  }
  return ids;
}

function readShowNumberingPref(): boolean {
  if (typeof window === 'undefined') {
    return true;
  }
  try {
    const raw = window.localStorage.getItem(SHOW_NUMBERING_KEY);
    if (!raw) {
      return true;
    }
    return JSON.parse(raw) === true;
  } catch {
    return true;
  }
}

function writeShowNumberingPref(value: boolean): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(SHOW_NUMBERING_KEY, JSON.stringify(value));
  } catch {
    // ignore storage write failures
  }
}

function readExpandedPref(rootDocumentId: number): number[] {
  if (typeof window === 'undefined') {
    return [];
  }
  try {
    const raw = window.localStorage.getItem(EXPANDED_BY_ROOT_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw) as Record<string, number[]>;
    const byRoot = parsed[String(rootDocumentId)];
    if (!Array.isArray(byRoot)) {
      return [];
    }
    return byRoot.filter((value): value is number => typeof value === 'number');
  } catch {
    return [];
  }
}

function writeExpandedPref(rootDocumentId: number, expandedIds: Set<number>): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    const raw = window.localStorage.getItem(EXPANDED_BY_ROOT_KEY);
    const parsed = raw ? (JSON.parse(raw) as Record<string, number[]>) : {};
    parsed[String(rootDocumentId)] = Array.from(expandedIds);
    window.localStorage.setItem(EXPANDED_BY_ROOT_KEY, JSON.stringify(parsed));
  } catch {
    // ignore storage write failures
  }
}

function findAncestorChain(root: DocumentHierarchyTreeNode | null | undefined, targetId: number): number[] {
  if (!root || typeof root.id !== 'number') {
    return [];
  }
  if (root.id === targetId) {
    return [root.id];
  }

  const stack: Array<{ node: DocumentHierarchyTreeNode; chain: number[] }> = [
    { node: root, chain: [root.id] }
  ];

  while (stack.length > 0) {
    const current = stack.pop();
    if (!current) {
      break;
    }

    const children = getNodeChildren(current.node);
    for (let index = children.length - 1; index >= 0; index -= 1) {
      const child = children[index];
      const nextChain = [...current.chain, child.id];

      if (child.id === targetId) {
        return nextChain;
      }

      stack.push({
        node: child,
        chain: nextChain
      });
    }
  }

  return [];
}

function fallbackTitle(documentId: number): string {
  return `Document #${documentId}`;
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'Action failed. Please try again.';
}

export function DocumentStructureSection({ document, collapsed, onToggleCollapsed }: DocumentStructureSectionProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigateWithContentFlush();
  const location = useLocation();
  const storeRootDocumentId = useDocumentScrollStore((s) => s.rootDocumentId);
  const storeActiveSectionId = useDocumentScrollStore((s) => s.activeSectionId);
  const requestScrollTo = useDocumentScrollStore((s) => s.requestScrollTo);
  const createChildMutation = useCreateDocumentChild();
  const createSiblingMutation = useCreateDocumentSibling();
  const moveNodeMutation = useMoveDocumentNode();
  const deleteSubtreeMutation = useDeleteDocumentSubtree();
  const treeRoot = document.hierarchyTree ?? null;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inlineInputRef = useRef<HTMLInputElement | null>(null);
  const [showNumbering, setShowNumbering] = useState<boolean>(readShowNumberingPref);
  const ancestorChain = useMemo(
    () => findAncestorChain(treeRoot, document.id),
    [document.id, treeRoot]
  );
  const treeIndex = useMemo(() => buildTreeIndex(treeRoot), [treeRoot]);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set<number>());
  const [inlineCreate, setInlineCreate] = useState<InlineCreateState | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState | null>(null);
  const [deleteBackendSummary, setDeleteBackendSummary] = useState<DeleteBackendSummary | null>(null);
  const [reparentDialogOpen, setReparentDialogOpen] = useState(false);
  const [reparentDocumentId, setReparentDocumentId] = useState<number | null>(null);
  const [reparentTargetParentId, setReparentTargetParentId] = useState<number | null>(null);
  const wasInlineCreateOpenRef = useRef(false);
  const resetDeleteState = useCallback(() => {
    setDeleteDialog(null);
    setDeleteBackendSummary(null);
  }, []);

  useEffect(() => {
    if (!treeRoot) {
      setExpandedIds(new Set<number>(ancestorChain));
      return;
    }
    const persisted = new Set<number>(readExpandedPref(treeRoot.id));
    ancestorChain.forEach((id) => persisted.add(id));
    setExpandedIds(persisted);
  }, [treeRoot, ancestorChain]);

  useEffect(() => {
    if (!treeRoot) {
      return;
    }
    writeExpandedPref(treeRoot.id, expandedIds);
  }, [expandedIds, treeRoot]);

  useEffect(() => {
    setShowNumbering(readShowNumberingPref());
  }, [document.rootCode]);

  useEffect(() => {
    if (!inlineCreate) {
      wasInlineCreateOpenRef.current = false;
      return;
    }
    if (wasInlineCreateOpenRef.current) {
      return;
    }
    wasInlineCreateOpenRef.current = true;
    inlineInputRef.current?.focus();
    inlineInputRef.current?.select();
  }, [inlineCreate]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    const currentNodeElement = container.querySelector<HTMLElement>(`[data-document-id="${document.id}"]`);
    currentNodeElement?.scrollIntoView({ block: 'nearest' });
  }, [document.id, treeRoot]);

  useEffect(() => {
    resetDeleteState();
  }, [document.id, document.rootCode, location.pathname, location.key, resetDeleteState]);

  const toggleExpanded = (documentId: number) => {
    setExpandedIds((previous) => {
      const next = new Set(previous);
      if (next.has(documentId)) {
        next.delete(documentId);
      } else {
        next.add(documentId);
      }
      return next;
    });
  };

  const expandAll = () => {
    const next = new Set<number>();
    treeIndex.forEach((entry, id) => {
      if (getNodeChildren(entry.node).length > 0) {
        next.add(id);
      }
    });
    setExpandedIds(next);
  };

  const collapseAll = () => {
    // Keep the active lineage expanded so current selection remains visible.
    setExpandedIds(new Set<number>(ancestorChain));
  };

  const jumpToCurrent = () => {
    const chain = findAncestorChain(treeRoot, document.id);
    setExpandedIds((previous) => {
      const next = new Set(previous);
      chain.forEach((id) => next.add(id));
      return next;
    });
    navigate(toContentHref(document.code));
  };

  const resolveContentRef = (id: number): string => {
    if (id === document.id) {
      return document.code;
    }
    const cached = queryClient.getQueryData<{ code?: string }>(contentKeys.detail(id));
    return typeof cached?.code === 'string' && cached.code.length > 0 ? cached.code : String(id);
  };

  const isBusy =
    createChildMutation.isPending ||
    createSiblingMutation.isPending ||
    moveNodeMutation.isPending ||
    deleteSubtreeMutation.isPending;

  // A template definition's structure is owned by the templates.* tools (TemplateSection rows
  // travel with each change); raw hierarchy mutations here would bypass them. The render's
  // template field is the backend's say-so.
  const structureRenderQuery = useDocumentRender(treeRoot?.id ?? document.id);
  const hierarchyReadOnly = structureRenderQuery.data?.template?.role === 'definition';

  const openInlineCreate = (mode: 'child' | 'sibling', nodeId: number) => {
    if (hierarchyReadOnly) return;
    const entry = treeIndex.get(nodeId);
    if (!entry) {
      // Fallback root row (no hierarchyTree payload yet): allow child creation under current document.
      if (mode === 'child' && nodeId === document.id) {
        setInlineCreate({
          mode,
          anchorId: nodeId,
          parentDocumentId: nodeId,
          sortOrder: 0,
          value: ''
        });
      }
      return;
    }
    if (entry.parentId === null && mode === 'sibling') {
      return;
    }
    const parentDocumentId = mode === 'child' ? nodeId : (entry.parentId as number);
    const sortOrder = mode === 'child'
      ? getNodeChildren(entry.node).length
      : entry.indexWithinParent + 1;
    setInlineCreate({
      mode,
      anchorId: nodeId,
      parentDocumentId,
      sortOrder,
      value: ''
    });
  };

  const submitInlineCreate = async () => {
    if (!inlineCreate || !inlineCreate.value.trim()) {
      return;
    }
    const title = inlineCreate.value.trim();
    try {
      if (inlineCreate.mode === 'child') {
        const result = await createChildMutation.mutateAsync({
          parentDocumentId: inlineCreate.parentDocumentId,
          title,
          sortOrder: inlineCreate.sortOrder
        });
        const chain = findAncestorChain(result.document.hierarchyTree, result.document.id);
        setExpandedIds((previous) => {
          const next = new Set(previous);
          chain.forEach((id) => next.add(id));
          return next;
        });
        setInlineCreate(null);
        navigate(toContentHref(result.document.code));
        return;
      }

      const result = await createSiblingMutation.mutateAsync({
        siblingDocumentId: inlineCreate.anchorId,
        title,
        sortOrder: inlineCreate.sortOrder
      });
      const chain = findAncestorChain(result.document.hierarchyTree, result.document.id);
      setExpandedIds((previous) => {
        const next = new Set(previous);
        chain.forEach((id) => next.add(id));
        return next;
      });
      setInlineCreate(null);
      navigate(toContentHref(result.document.code));
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const moveNode = async (documentId: number, targetParentDocumentId: number, sortOrder?: number) => {
    if (hierarchyReadOnly) return;
    const result = await moveNodeMutation.mutateAsync({ documentId, targetParentDocumentId, sortOrder });
    const chain = findAncestorChain(result.document.hierarchyTree, documentId);
    setExpandedIds((previous) => {
      const next = new Set(previous);
      chain.forEach((id) => next.add(id));
      return next;
    });
  };

  const handleMoveUp = async (documentId: number) => {
    const entry = treeIndex.get(documentId);
    if (!entry || entry.parentId === null || entry.indexWithinParent === 0) {
      return;
    }
    await moveNode(documentId, entry.parentId, entry.indexWithinParent - 1);
  };

  const handleMoveDown = async (documentId: number) => {
    const entry = treeIndex.get(documentId);
    if (!entry || entry.parentId === null || entry.indexWithinParent >= entry.siblingCount - 1) {
      return;
    }
    await moveNode(documentId, entry.parentId, entry.indexWithinParent + 1);
  };

  const openReparentDialog = (documentId: number) => {
    const entry = treeIndex.get(documentId);
    if (!entry || entry.parentId === null) {
      return;
    }
    setReparentDocumentId(documentId);
    setReparentTargetParentId(entry.parentId);
    setReparentDialogOpen(true);
  };

  const reparentOptions = useMemo(() => {
    if (!treeRoot || reparentDocumentId === null) {
      return [];
    }
    const movingNode = findNode(treeRoot, reparentDocumentId);
    const blockedIds = new Set<number>(collectSubtreeIds(movingNode));
    const options: Array<{ id: number; label: string }> = [];
    const traversalIds = collectTreeNodeIdsPreOrder(treeRoot);
    traversalIds.forEach((id) => {
      const entry = treeIndex.get(id);
      if (!entry) {
        return;
      }
      if (blockedIds.has(id)) {
        return;
      }
      const labelNumber = showNumbering ? `${entry.node.outlineNumber} ` : '';
      options.push({
        id,
        label: `${labelNumber}${entry.node.title || fallbackTitle(id)}`
      });
    });
    return options;
  }, [treeRoot, treeIndex, reparentDocumentId, showNumbering]);

  const confirmReparent = async () => {
    if (reparentDocumentId === null || reparentTargetParentId === null) {
      return;
    }
    await moveNode(reparentDocumentId, reparentTargetParentId);
    setReparentDialogOpen(false);
    setReparentDocumentId(null);
    setReparentTargetParentId(null);
  };

  const openDeleteDialog = (documentId: number) => {
    if (hierarchyReadOnly) return;
    const node = findNode(treeRoot, documentId);
    if (!node) {
      if (!treeRoot && documentId === document.id) {
        setDeleteBackendSummary(null);
        setDeleteDialog({
          documentId,
          title: document.title || fallbackTitle(document.id),
          estimatedCount: 1,
          subtreeIds: [document.id]
        });
      }
      return;
    }
    const subtreeIds = collectSubtreeIds(node);
    setDeleteBackendSummary(null);
    setDeleteDialog({
      documentId,
      title: node.title || fallbackTitle(node.id),
      estimatedCount: subtreeIds.length,
      subtreeIds
    });
  };

  const confirmDeleteSubtree = async () => {
    if (!deleteDialog) {
      return;
    }
    const result = await deleteSubtreeMutation.mutateAsync(deleteDialog.documentId);
    setDeleteBackendSummary(result.warning);
    if (deleteDialog.subtreeIds.includes(document.id)) {
      resetDeleteState();
      const rootIdForNav = treeRoot?.id ?? document.id;
      if (deleteDialog.documentId === rootIdForNav) {
        navigate('/explore');
      } else {
        navigate(toContentHref(resolveContentRef(rootIdForNav)));
      }
    }
  };

  const isRootDeleteTarget = deleteDialog?.documentId === (treeRoot?.id ?? document.id);

  const onInlineCreateKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      void submitInlineCreate();
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      setInlineCreate(null);
    }
  };

  const renderNode = (node: DocumentHierarchyTreeNode, depth: number): ReactNode => {
    const children = getNodeChildren(node);
    const hasChildren = children.length > 0;
    const isExpanded = expandedIds.has(node.id);
    const documentRootId = treeRoot?.id ?? document.id;
    const storeIsForThisDocument =
      storeRootDocumentId !== null && storeRootDocumentId === documentRootId;
    const activeNodeId = storeIsForThisDocument && storeActiveSectionId !== null
      ? storeActiveSectionId
      : document.id;
    const isCurrentNode = node.id === activeNodeId;
    const entry = treeIndex.get(node.id);
    const isRootNode = entry?.parentId === null;
    const canMoveUp = Boolean(entry && entry.parentId !== null && entry.indexWithinParent > 0);
    const canMoveDown = Boolean(
      entry &&
      entry.parentId !== null &&
      entry.indexWithinParent < entry.siblingCount - 1
    );

    const showDocumentIcon = depth === 0;
    const rowPaddingLeftRem = 0.75 + depth * 0.75;

    return (
      <li key={node.id}>
        <div
          data-document-id={node.id}
          className={cn(
            'group -mr-1 relative flex items-center rounded-md pr-1 transition-colors',
            isCurrentNode ? 'bg-muted text-primary border-l-4 border-primary' : 'hover:bg-accent text-muted-foreground'
          )}
          style={{ paddingLeft: `${rowPaddingLeftRem}rem` }}
        >
          {showDocumentIcon ? (
            <FileText className="mr-1.5 h-4 w-4 shrink-0 text-muted-foreground" />
          ) : (
            <span className="mr-1.5 w-0 shrink-0" />
          )}

          <button
            type="button"
            className={cn(
              'min-w-0 flex-1 truncate py-2 pr-16 text-left text-sm',
              isCurrentNode ? 'font-medium text-primary' : 'text-muted-foreground hover:text-foreground'
            )}
            onClick={() => {
              const sameRoot = treeRoot
                ? (treeRoot.id ?? document.id) === treeRoot.id
                : false;
              if (sameRoot && storeIsForThisDocument) {
                requestScrollTo(node.id);
                return;
              }
              navigate(toContentHref(resolveContentRef(node.id)));
            }}
            title={node.title || fallbackTitle(node.id)}
          >
            {showNumbering ? <span className="mr-1.5 text-xs text-muted-foreground">{node.outlineNumber}</span> : null}
            {node.title || fallbackTitle(node.id)}
          </button>
          <div className="absolute right-0 top-1/2 flex -translate-y-1/2 items-center gap-0.5 pr-0 opacity-100">
            {hierarchyReadOnly ? null : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn(
                    'h-7 w-7 shrink-0 opacity-70 transition-opacity hover:opacity-100',
                    isCurrentNode ? 'opacity-100' : ''
                  )}
                  disabled={isBusy}
                  aria-label="Document row actions"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => openInlineCreate('child', node.id)}>
                  Add child
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => openInlineCreate('sibling', node.id)}
                  disabled={isRootNode}
                >
                  Add sibling
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => void handleMoveUp(node.id)} disabled={!canMoveUp}>
                  Move up
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => void handleMoveDown(node.id)} disabled={!canMoveDown}>
                  Move down
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => openReparentDialog(node.id)} disabled={isRootNode}>
                  Move to parent...
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() => openDeleteDialog(node.id)}
                >
                  Delete subtree
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            )}
            {hasChildren ? (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0 opacity-70 transition-opacity hover:opacity-100"
                onClick={() => toggleExpanded(node.id)}
                aria-label={isExpanded ? 'Collapse node' : 'Expand node'}
              >
                <ChevronRight className={cn('h-4 w-4 transition-transform', isExpanded && 'rotate-90')} />
              </Button>
            ) : null}
          </div>
        </div>

        {inlineCreate && inlineCreate.anchorId === node.id ? (
          <div
            className="py-1"
            style={{
              paddingLeft: `${(inlineCreate.mode === 'child' ? depth + 1 : depth) * 0.75}rem`
            }}
          >
            <div className="flex items-center gap-1">
              <input
                ref={inlineInputRef}
                type="text"
                className="h-7 min-w-0 flex-1 rounded-md border bg-background px-2 text-xs"
                value={inlineCreate.value}
                onChange={(event) =>
                  setInlineCreate((previous) => previous ? { ...previous, value: event.target.value } : previous)
                }
                onKeyDown={onInlineCreateKeyDown}
                placeholder={inlineCreate.mode === 'child' ? 'New child title' : 'New sibling title'}
                disabled={isBusy}
              />
              <Button
                size="sm"
                className="h-7 w-7 p-0 shrink-0"
                onClick={() => void submitInlineCreate()}
                disabled={!inlineCreate.value.trim() || isBusy}
                aria-label="Create document"
                title="Create"
              >
                <Check className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 w-7 p-0 shrink-0"
                onClick={() => setInlineCreate(null)}
                disabled={isBusy}
                aria-label="Cancel inline creation"
                title="Cancel"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ) : null}

        {hasChildren && isExpanded ? (
          <ul className="space-y-0.5">{children.map((childNode) => renderNode(childNode, depth + 1))}</ul>
        ) : null}
      </li>
    );
  };

  return (
    <section data-testid="sidebar-section-contents" data-collapsed={collapsed ? 'true' : 'false'}>
      <div ref={containerRef} className="space-y-1" data-testid="document-structure-sidebar">
        <div className="flex items-center justify-between pl-2 pr-0 pt-3 pb-1">
          <p className="text-sm font-medium text-muted-foreground">Contents</p>
          <div className="flex items-center -mr-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Contents options">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuCheckboxItem
                  checked={showNumbering}
                  onCheckedChange={(checked) => {
                    const next = checked === true;
                    setShowNumbering(next);
                    writeShowNumberingPref(next);
                  }}
                >
                  Show numbering
                </DropdownMenuCheckboxItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={expandAll}>Expand all</DropdownMenuItem>
                <DropdownMenuItem onClick={collapseAll}>Collapse all</DropdownMenuItem>
                <DropdownMenuItem onClick={jumpToCurrent}>Jump to current</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              data-testid="sidebar-toggle-contents"
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              aria-expanded={!collapsed}
              aria-label={collapsed ? 'Expand contents' : 'Collapse contents'}
              onClick={onToggleCollapsed}
            >
              <ChevronDown className={cn('h-4 w-4 transition-transform', collapsed && '-rotate-90')} />
            </Button>
          </div>
        </div>
        {!collapsed ? (
          treeRoot ? (
            <ul data-testid="sidebar-contents-list" className="space-y-0.5">{renderNode(treeRoot, 0)}</ul>
          ) : (
            <>
            <div className="group -mr-1 relative flex items-center rounded-md pl-3 pr-1 transition-colors hover:bg-accent text-muted-foreground">
                <FileText className="mr-1.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <button
                  type="button"
                  className="min-w-0 flex-1 truncate py-2 pr-16 text-left text-sm text-muted-foreground hover:text-foreground"
                  onClick={() => navigate(toContentHref(document.code))}
                  title={document.title || fallbackTitle(document.id)}
                >
                  {showNumbering ? <span className="mr-1.5 text-xs text-muted-foreground">1</span> : null}
                  {document.title || fallbackTitle(document.id)}
                </button>
              <div className="absolute right-0 top-1/2 flex -translate-y-1/2 items-center gap-0.5 pr-0 opacity-100">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 shrink-0 opacity-70 transition-opacity hover:opacity-100"
                        disabled={isBusy}
                        aria-label="Document row actions"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openInlineCreate('child', document.id)}>
                        Add child
                      </DropdownMenuItem>
                      <DropdownMenuItem disabled>
                        Add sibling
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem disabled>
                        Move up
                      </DropdownMenuItem>
                      <DropdownMenuItem disabled>
                        Move down
                      </DropdownMenuItem>
                      <DropdownMenuItem disabled>
                        Move to parent...
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onClick={() => openDeleteDialog(document.id)}
                      >
                        Delete subtree
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              {inlineCreate && inlineCreate.anchorId === document.id ? (
                <div className="py-1 pl-3">
                  <div className="flex items-center gap-1">
                    <input
                      ref={inlineInputRef}
                      type="text"
                      className="h-7 min-w-0 flex-1 rounded-md border bg-background px-2 text-xs"
                      value={inlineCreate.value}
                      onChange={(event) =>
                        setInlineCreate((previous) => previous ? { ...previous, value: event.target.value } : previous)
                      }
                      onKeyDown={onInlineCreateKeyDown}
                      placeholder="New child title"
                      disabled={isBusy}
                    />
                    <Button
                      size="sm"
                      className="h-7 w-7 p-0 shrink-0"
                      onClick={() => void submitInlineCreate()}
                      disabled={!inlineCreate.value.trim() || isBusy}
                      aria-label="Create document"
                      title="Create"
                    >
                      <Check className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 w-7 p-0 shrink-0"
                      onClick={() => setInlineCreate(null)}
                      disabled={isBusy}
                      aria-label="Cancel inline creation"
                      title="Cancel"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ) : null}
            </>
          )
        ) : null}
      </div>
      <Dialog open={reparentDialogOpen} onOpenChange={setReparentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Move document to parent</DialogTitle>
            <DialogDescription>
              Select a new parent in the current hierarchy root. Invalid targets are excluded.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-3">
            <select
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              value={reparentTargetParentId ?? ''}
              onChange={(event) => setReparentTargetParentId(Number(event.target.value))}
              disabled={isBusy}
            >
              {reparentOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </DialogBody>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setReparentDialogOpen(false)} disabled={isBusy}>
              Cancel
            </Button>
            <Button onClick={() => void confirmReparent()} disabled={isBusy || reparentTargetParentId === null}>
              Move
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={Boolean(deleteDialog)} onOpenChange={(open) => !open && resetDeleteState()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isRootDeleteTarget ? 'Delete entire document hierarchy' : 'Delete subtree'}</DialogTitle>
            <DialogDescription>
              {isRootDeleteTarget
                ? 'This permanently deletes the root section and every descendant section in this document.'
                : 'This permanently deletes the selected section and all descendants.'}
            </DialogDescription>
          </DialogHeader>
          {deleteDialog ? (
            <>
              <DialogBody className="space-y-3 text-sm">
                <Notice tone="destructive" className="p-2">
                  {isRootDeleteTarget
                    ? 'This action removes the entire document hierarchy and cannot be undone.'
                    : 'Only this section branch will be removed from the document hierarchy.'}
                </Notice>
                <p>
                  Target: <span className="font-medium">{deleteDialog.title}</span>
                </p>
                <p>
                  Estimated affected documents: <span className="font-medium">{deleteDialog.estimatedCount}</span>
                </p>
                {deleteBackendSummary ? (
                  <p className="rounded-md border bg-muted p-2 text-muted-foreground">
                    {`Deleted ${deleteBackendSummary.deletedCount} documents${deleteBackendSummary.deletedTitle ? ` from "${deleteBackendSummary.deletedTitle}"` : ''} (root #${deleteBackendSummary.deletedRootId}).`}
                  </p>
                ) : null}
              </DialogBody>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={resetDeleteState} disabled={isBusy}>
                  {deleteBackendSummary ? 'Close' : 'Cancel'}
                </Button>
                {!deleteBackendSummary ? (
                  <Button
                    variant="destructive"
                    onClick={() => void confirmDeleteSubtree()}
                    disabled={isBusy}
                  >
                    {isRootDeleteTarget ? 'Delete entire hierarchy' : 'Delete subtree'}
                  </Button>
                ) : null}
              </DialogFooter>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </section>
  );
}

